import {
  MessageBoxWidgetDto,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetDto,
} from "@intelligems/ig-types";
import { hexToHex8 } from "@intelligems/ig-utils";
import { Theme } from "@mui/material";
import {
  isMessageBox,
  isProgressBar,
  isQuantityButtons,
} from "@src/store/slices/widgets/util";

import { getAllColorFields } from "./colorHierarchy";
import {
  getProgressBarBarFieldConfigs,
  getProgressBarBreakpointFieldConfigs,
  getQuantityButtonsBackgroundFieldConfigs,
} from "./fields";
import { updateDraftStyles, updateStyleOverrides } from "./styleOverrides";

const getContrastTextFieldsForWidget = (
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme
) => {
  const themeColors = {
    black: theme.palette.common.black,
    white: theme.palette.common.white,
  };
  const themeObject = { palette: { common: themeColors } };

  const contrastTextFields: Array<{
    key: string;
    section: string;
    opacity?: number;
  }> = [];

  if (isQuantityButtons(widget)) {
    const textColorFields = [
      { key: "textColors.badge", useContrastText: true },
      { key: "textColors.freeShippingLabel", useContrastText: true },
      { key: "textColors.freeGiftLabel", useContrastText: true },
    ];

    textColorFields.forEach((config) => {
      if (config.useContrastText) {
        const [section, field] = config.key.split(".");
        contrastTextFields.push({
          key: field,
          section,
        });
      }
    });
  }

  if (isProgressBar(widget)) {
    const breakpointFieldConfigs = getProgressBarBreakpointFieldConfigs(
      widget,
      themeObject
    );

    breakpointFieldConfigs.forEach((config) => {
      if (config.useContrastText) {
        const [section, field] = config.key.split(".");
        contrastTextFields.push({
          key: field,
          section,
          opacity: config.useContrastTextOpacity,
        });
      }
    });
  }

  if (isMessageBox(widget)) {
    contrastTextFields.push({
      key: "textColor",
      section: "container",
    });
  }

  return contrastTextFields;
};

const applyContrastTextToFields = (
  target: any,
  contrastTextFields: Array<{ key: string; section: string; opacity?: number }>,
  color: string,
  theme: Theme,
  isDraft: boolean
) => {
  const contrastTextColor = getContrastText(color, theme);

  const fieldsBySection = contrastTextFields.reduce(
    (acc, field) => {
      if (!acc[field.section]) acc[field.section] = [];
      acc[field.section].push(field);
      return acc;
    },
    {} as Record<
      string,
      Array<{ key: string; section: string; opacity?: number }>
    >
  );

  Object.entries(fieldsBySection).forEach(([section, fields]) => {
    fields.forEach((field) => {
      const finalColor = field.opacity
        ? hexToHex8(contrastTextColor, field.opacity)
        : contrastTextColor;

      if (isDraft) {
        updateDraftStyles(target, `${section}.${field.key}`, finalColor);
      } else {
        target = updateStyleOverrides(
          target,
          `${section}.${field.key}`,
          finalColor
        );
      }
    });
  });

  return target;
};

const rgbaToHex = (rgba: string): string => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (!match) {
    if (rgba.startsWith("#")) {
      return rgba;
    }
    return "#000000";
  }

  const [, r, g, b] = match;
  const toHex = (n: string) => parseInt(n, 10).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getContrastText = (backgroundColor: string, theme: Theme): string => {
  const contrastColor = theme.palette.getContrastText(backgroundColor);
  return rgbaToHex(contrastColor);
};

const getOpacityFieldConfig = (
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme
): Record<string, { defaultColor: string; opacity: number }> => {
  const themeColors = {
    black: theme.palette.common.black,
    white: theme.palette.common.white,
  };

  const themeObject = { palette: { common: themeColors } };

  const opacityConfig: Record<
    string,
    { defaultColor: string; opacity: number }
  > = {};

  if (isQuantityButtons(widget)) {
    const fieldConfigs = getQuantityButtonsBackgroundFieldConfigs(
      widget.config?.desktop?.variant,
      undefined,
      themeObject
    );
    fieldConfigs.forEach((config) => {
      if (config.useOpacity) {
        opacityConfig[config.key] = config.useOpacity;
      }
    });
  }

  if (isProgressBar(widget)) {
    const barFieldConfigs = getProgressBarBarFieldConfigs(widget, themeObject);
    const breakpointFieldConfigs = getProgressBarBreakpointFieldConfigs(
      widget,
      themeObject
    );

    [...barFieldConfigs, ...breakpointFieldConfigs].forEach((config) => {
      if ("useOpacity" in config && config.useOpacity) {
        if (
          config.key !== "breakpoint.inactiveIconColor" &&
          config.key !== "breakpoint.activeIconColor" &&
          config.key !== "breakpoint.inactiveColor" &&
          config.key !== "breakpoint.activeColor"
        ) {
          opacityConfig[config.key] = config.useOpacity;
        }
      }
    });
  }

  return opacityConfig;
};

const getColorValueForField = (
  fieldPath: string,
  color: string,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme
): string => {
  const opacityFieldConfig = getOpacityFieldConfig(widget, theme);
  const opacityConfig = opacityFieldConfig[fieldPath];
  if (opacityConfig) {
    return hexToHex8(color, opacityConfig.opacity);
  }
  return color;
};

const updateColorSection = (
  target: any,
  sectionName: string,
  fields: string[],
  color: string,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme,
  isDraft: boolean = true
) => {
  fields.forEach((field) => {
    const fieldPath = `${sectionName}.${field}`;
    const colorValue = getColorValueForField(fieldPath, color, widget, theme);

    if (isDraft) {
      updateDraftStyles(target, fieldPath, colorValue);
    } else {
      target = updateStyleOverrides(target, fieldPath, colorValue);
    }
  });
  return target;
};

const updateWidgetColors = (
  target: any,
  color: string,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme,
  isDraft: boolean = true
) => {
  const colorFields = getAllColorFields(widget);

  if (isQuantityButtons(widget)) {
    if (colorFields.backgroundColors) {
      target = updateColorSection(
        target,
        "backgroundColors",
        colorFields.backgroundColors,
        color,
        widget,
        theme,
        isDraft
      );
    }
    if (colorFields.textColors) {
      target = updateColorSection(
        target,
        "textColors",
        colorFields.textColors,
        color,
        widget,
        theme,
        isDraft
      );
    }
  }

  if (isProgressBar(widget)) {
    if (colorFields.bar) {
      target = updateColorSection(
        target,
        "bar",
        colorFields.bar,
        color,
        widget,
        theme,
        isDraft
      );
    }
    if (colorFields.breakpoint) {
      target = updateColorSection(
        target,
        "breakpoint",
        colorFields.breakpoint,
        color,
        widget,
        theme,
        isDraft
      );
    }
    if (colorFields.textColors) {
      target = updateColorSection(
        target,
        "textColors",
        colorFields.textColors,
        color,
        widget,
        theme,
        isDraft
      );
    }
  }

  if (isMessageBox(widget)) {
    if (colorFields.container) {
      target = updateColorSection(
        target,
        "container",
        ["backgroundColor"],
        color,
        widget,
        theme,
        isDraft
      );
    }
  }

  const contrastTextFields = getContrastTextFieldsForWidget(widget, theme);
  target = applyContrastTextToFields(
    target,
    contrastTextFields,
    color,
    theme,
    isDraft
  );

  return target;
};

export const updateAllColorsInDraft = (
  draft: any,
  color: string,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme
) => {
  updateWidgetColors(draft, color, widget, theme, true);
};

export const createBatchedColorUpdates = (
  currentStyleOverrides: any,
  color: string,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  theme: Theme
) => {
  const batchedUpdates = {
    ...currentStyleOverrides,
    colors: {
      ...currentStyleOverrides.colors,
      primary: color,
    },
  };

  return updateWidgetColors(batchedUpdates, color, widget, theme, false);
};

export const getFieldKey = (field: string, prefix: string): string => {
  return field.replace(`${prefix}.`, "");
};

const extractBaseHexColor = (color: string): string => {
  if (color.match(/^#[0-9a-fA-F]{8}$/)) {
    return color.substring(0, 7);
  }
  return color;
};

export const getColorPickerValue = (
  field: string,
  styleConfig: any,
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto,
  defaultColor: string,
  theme: Theme
): string => {
  if (field === "theme.picker") {
    if (isProgressBar(widget)) {
      return styleConfig.styles?.breakpoint?.activeColor || defaultColor;
    } else if (isMessageBox(widget)) {
      return styleConfig.styles?.container?.backgroundColor || defaultColor;
    } else {
      return styleConfig.styles?.backgroundColors?.badgeColor || defaultColor;
    }
  } else if (field.startsWith("colors.")) {
    const key = getFieldKey(field, "colors");
    return styleConfig.styles?.colors?.[key] || defaultColor;
  } else if (field.startsWith("backgroundColors.")) {
    const key = getFieldKey(field, "backgroundColors");
    const storedColor =
      styleConfig.styles?.backgroundColors?.[key] || defaultColor;
    if (storedColor && storedColor.match(/^#[0-9a-fA-F]{8}$/)) {
      return storedColor;
    }
    return extractBaseHexColor(storedColor);
  } else if (field.startsWith("textColors.")) {
    const key = getFieldKey(field, "textColors");
    const storedColor = styleConfig.styles?.textColors?.[key] || defaultColor;
    if (storedColor && storedColor.match(/^#[0-9a-fA-F]{8}$/)) {
      return storedColor;
    }
    return extractBaseHexColor(storedColor);
  } else if (field.startsWith("bar.")) {
    const key = getFieldKey(field, "bar");
    const storedColor = styleConfig.styles?.bar?.[key] || defaultColor;
    if (storedColor && storedColor.match(/^#[0-9a-fA-F]{8}$/)) {
      return storedColor;
    }
    return extractBaseHexColor(storedColor);
  } else if (field.startsWith("breakpoint.")) {
    const key = getFieldKey(field, "breakpoint");
    const storedColor = styleConfig.styles?.breakpoint?.[key] || defaultColor;

    if (
      key === "activeIconColor" ||
      key === "inactiveIconColor" ||
      key === "activeColor" ||
      key === "inactiveColor"
    ) {
      return storedColor;
    }

    if (storedColor && storedColor.match(/^#[0-9a-fA-F]{8}$/)) {
      return storedColor;
    }
    return extractBaseHexColor(storedColor);
  } else if (field.startsWith("container.")) {
    const key = getFieldKey(field, "container");
    const storedColor = styleConfig.styles?.container?.[key] || defaultColor;
    if (storedColor && storedColor.match(/^#[0-9a-fA-F]{8}$/)) {
      return storedColor;
    }
    return extractBaseHexColor(storedColor);
  }

  return defaultColor;
};
